import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material';
import { CheckCircle, CreditCard, Lock } from '@mui/icons-material';
import { LocalizedButton as Button, LocalizedChip as Chip } from './i18n/LocalizedMui';
import Typography from './i18n/LocalizedTypography';
import { studioRequest } from '../api/studioClient';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, callback: (response: any) => void) => void;
    };
  }
}

type PaymentSuccess = {
  paymentId?: string;
  subscriptionId?: string;
  planType: 'pro_yearly';
};

type Props = {
  open: boolean;
  onClose: () => void;
  onPaymentSuccess: (payment: PaymentSuccess) => void;
};

type CheckoutPayload = {
  alreadyActive?: boolean;
  keyId?: string;
  subscriptionId?: string;
};

const loadRazorpayCheckout = async () => {
  if (window.Razorpay) return;

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-kundlistar-razorpay]');
    const script = existing || document.createElement('script');
    if (!existing) {
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.dataset.kundlistarRazorpay = 'true';
      document.body.appendChild(script);
    }
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Razorpay checkout could not be loaded.')), { once: true });
  });

  if (!window.Razorpay) throw new Error('Razorpay checkout could not be loaded.');
};

const PaymentComponent: React.FC<Props> = ({ open, onClose, onPaymentSuccess }) => {
  const { state, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  const startCheckout = async () => {
    try {
      setLoading(true);
      setError('');
      await loadRazorpayCheckout();

      const checkout = await studioRequest<CheckoutPayload>({
        url: '/payments/create-order',
        method: 'POST',
        data: { planType: 'premium' },
      }, 'Unable to start Razorpay checkout');

      if (checkout.alreadyActive) {
        await refreshUser();
        onPaymentSuccess({ subscriptionId: checkout.subscriptionId, planType: 'pro_yearly' });
        return;
      }
      if (!checkout.keyId || !checkout.subscriptionId || !window.Razorpay) {
        throw new Error('Razorpay did not return a valid yearly checkout.');
      }

      const razorpay = new window.Razorpay({
        key: checkout.keyId,
        name: 'Kundlistar Studio',
        description: 'Studio Pro Yearly — ₹999/year',
        subscription_id: checkout.subscriptionId,
        prefill: { name: state.user?.name || '', email: state.user?.email || '' },
        theme: { color: '#0F6E56' },
        modal: { ondismiss: () => setLoading(false) },
        handler: async (response: any) => {
          try {
            const verified = await studioRequest<PaymentSuccess>({
              url: '/payments/verify-payment',
              method: 'POST',
              data: {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            }, 'Payment verification failed');
            await refreshUser();
            onPaymentSuccess(verified);
          } catch (verificationError: any) {
            setError(verificationError.message || 'Payment verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
      });

      razorpay.on('payment.failed', (response: any) => {
        setError(response?.error?.description || 'Payment failed. Please try again.');
        setLoading(false);
      });
      razorpay.open();
    } catch (checkoutError: any) {
      setError(checkoutError.message || 'Unable to open checkout.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreditCard color="primary" /> Complete Your Upgrade
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          <Box>
            <Chip label="YEARLY ONLY" color="primary" size="small" sx={{ mb: 1.5 }} />
            <Typography variant="h5" fontWeight={700}>Studio Pro Yearly</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
              <Typography variant="h3" color="primary.main" fontWeight={700}>₹999</Typography>
              <Typography color="text.secondary">/ year</Typography>
            </Box>
          </Box>
          <Alert severity="success" icon={<CheckCircle />}>
            Payment verification instantly activates Pro on your Studio account, including the app when you sign in with the same account.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <Lock fontSize="small" />
            <Typography variant="body2">Secure recurring yearly checkout powered by Razorpay.</Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>Not now</Button>
        <Button variant="contained" onClick={startCheckout} disabled={loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CreditCard />}>
          {loading ? 'Opening checkout…' : 'Pay ₹999 with Razorpay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentComponent;
